import "./styles.css";
import { NetworkClient } from "./net/NetworkClient";
import { GameRenderer } from "./render/GameRenderer";
import { UiController } from "./ui/UiController";
import type { Direction, SnakeState } from "./types/state";

const ROOM_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const ROOM_CODE_LENGTH = 6;

void bootstrap();

async function bootstrap(): Promise<void> {
  let currentState: SnakeState | null = null;
  let myIndex = -1;
  let activeRoomCode = "";

  const renderer = new GameRenderer();
  const canvasMount = document.getElementById("canvasMount");
  if (!canvasMount) throw new Error("Elemento #canvasMount não encontrado.");
  await renderer.mount(canvasMount);

  let network: NetworkClient;

  const ui = new UiController({
    onCreateRoom() {
      goToRoom(makeRoomCode());
    },
    onEnterRoom(code) {
      goToRoom(code);
    },
    onJoin(name, colorId) {
      network.join(name, colorId);
    },
    onLeave() {
      myIndex = -1;
      network.leaveGame();
    },
    onDirection(direction: Direction) {
      network.setDirection(direction);
    },
    onSpeed(level) {
      network.setSpeed(level);
    },
    onWalls(enabled) {
      network.setWalls(enabled);
    },
    onStart() {
      network.start();
    },
    onRestart() {
      network.restart();
    }
  });

  network = new NetworkClient({
    onConnectChange(online, label) {
      ui.setConnection(online, label);
    },
    onAssign(playerIndex) {
      myIndex = playerIndex;
      ui.setMyIndex(playerIndex);
    },
    onState(state) {
      currentState = state;
      ui.setState(state);
      renderer.render(state);
    },
    onNotice(message) {
      ui.setMessage(message);
    },
    onError(message) {
      ui.setMessage(message);
    }
  });

  window.addEventListener("resize", () => renderer.render(currentState));
  window.addEventListener("popstate", () => enterCurrentRoute());
  enterCurrentRoute();

  function animationLoop(): void {
    renderer.render(currentState);
    window.requestAnimationFrame(animationLoop);
  }
  window.requestAnimationFrame(animationLoop);

  function enterCurrentRoute(): void {
    const roomCode = parseRoomCodeFromPath();
    if (!roomCode) {
      activeRoomCode = "";
      currentState = null;
      myIndex = -1;
      network.disconnect();
      ui.clearRoom();
      renderer.render(null);
      return;
    }

    if (roomCode === activeRoomCode) return;
    activeRoomCode = roomCode;
    currentState = null;
    myIndex = -1;
    ui.setRoom(roomCode, roomUrl(roomCode));
    ui.setConnection(false, "conectando...");
    renderer.render(null);
    void network.connect(roomCode);
  }

  function goToRoom(code: string): void {
    const roomCode = sanitizeRoomCode(code);
    if (!roomCode) return;
    window.history.pushState({}, "", roomPath(roomCode));
    enterCurrentRoute();
  }
}

function parseRoomCodeFromPath(): string {
  const base = basePath();
  let path = window.location.pathname;
  if (base !== "/" && path.startsWith(base)) path = path.slice(base.length) || "/";
  const match = path.match(/^\/?room\/([A-Za-z0-9]{4,12})\/?$/);
  return sanitizeRoomCode(match?.[1] || "");
}

function roomPath(code: string): string {
  const base = basePath().replace(/\/$/, "");
  return `${base}/room/${sanitizeRoomCode(code)}`;
}

function roomUrl(code: string): string {
  return `${window.location.origin}${roomPath(code)}`;
}

function basePath(): string {
  const raw = String(import.meta.env.BASE_URL || "/");
  if (!raw.startsWith("/")) return `/${raw}`;
  return raw;
}

function makeRoomCode(): string {
  let code = "";
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) {
    code += ROOM_CODE_ALPHABET[Math.floor(Math.random() * ROOM_CODE_ALPHABET.length)];
  }
  return code;
}

function sanitizeRoomCode(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, ROOM_CODE_LENGTH);
}
